// src/components/UserMeet/InviteParticipantsModal.jsx
import React, { useState, useMemo } from "react";
import Icon from "@/pages/MeetingRoom/ui/Icon";
import { getAvatarColors } from "@/pages/MeetingRoom/utils/avatar";

export default function InviteParticipantsModal({
  open,
  onClose,
  onInvite,
  loading,
  participants = [],       // all participants in the main meeting
  breakoutParticipants = [], // already in the breakout
  myUserId,
}) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState("");

  // Filter out: self, already in breakout, in private meet
  const inviteableList = useMemo(() => {
    return participants.filter((p) => {
      if (!p.userId && !p._id && !p.id) return false;
      const uid = String(p.userId || p._id || p.id);

      // Not self
      if (myUserId && uid === String(myUserId)) return false;

      // Not already in breakout
      const alreadyIn = breakoutParticipants.some(
        (bp) => String(bp.userId) === uid
      );
      if (alreadyIn) return false;

      // Not in another private meet
      if (p.participantStatus === "in_private_meet") return false;

      return true;
    });
  }, [participants, breakoutParticipants, myUserId]);

  const filtered = useMemo(() => {
    if (!search.trim()) return inviteableList;
    const q = search.toLowerCase();
    return inviteableList.filter((p) =>
      (p.name || "").toLowerCase().includes(q)
    );
  }, [inviteableList, search]);

  const toggleSelect = (uid) => {
    setSelectedIds((prev) =>
      prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]
    );
  };

  const handleInvite = () => {
    if (selectedIds.length === 0) return;
    onInvite?.(selectedIds);
    setSelectedIds([]);
  };

  if (!open) return null;

  return (
    <div
      className="jt-modalOverlay"
      role="dialog"
      aria-modal="true"
      onMouseDown={onClose}
    >
      <div
        className="jt-sheet"
        style={{ maxWidth: 460 }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="jt-sheetHead">
          <div className="jt-sheetTitle">
            <span style={{ marginRight: 8 }}>👥</span>
            Invite People
          </div>
          <button className="jt-sheetClose" onClick={onClose}>
            <Icon name="close" />
          </button>
        </div>

        <div className="jt-sheetBody">
          {/* Search */}
          <div style={{ marginBottom: 14 }}>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search participants..."
              className="gm-search-input"
              style={{ width: "100%", boxSizing: "border-box" }}
            />
          </div>

          {/* Participant List */}
          <div
            style={{
              maxHeight: 320,
              overflowY: "auto",
              display: "grid",
              gap: 8,
              marginBottom: 16,
            }}
          >
            {filtered.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: "32px 0",
                  opacity: 0.6,
                  fontSize: 14,
                }}
              >
                {inviteableList.length === 0
                  ? "No participants available to invite"
                  : "No participants match your search"}
              </div>
            )}

            {filtered.map((p) => {
              const uid = String(p.userId || p._id || p.id);
              const selected = selectedIds.includes(uid);
              const [c1, c2] = getAvatarColors(p.name);

              return (
                <div
                  key={uid}
                  onClick={() => toggleSelect(uid)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 14px",
                    borderRadius: 12,
                    border: "1px solid",
                    borderColor: selected
                      ? "rgba(74,222,128,0.5)"
                      : "rgba(255,255,255,0.08)",
                    background: selected
                      ? "rgba(74,222,128,0.1)"
                      : "rgba(255,255,255,0.03)",
                    cursor: "pointer",
                    transition: "all 0.18s",
                  }}
                >
                  {/* Avatar */}
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      background: `linear-gradient(135deg, ${c1}, ${c2})`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      color: "#fff",
                      fontSize: 16,
                      flexShrink: 0,
                      overflow: "hidden",
                    }}
                  >
                    {p.image ? (
                      <img
                        src={p.image}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      (p.name?.[0] || "?").toUpperCase()
                    )}
                  </div>

                  {/* Name */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {p.name}
                    </div>
                    {p.networkingRole && (
                      <div style={{ fontSize: 12, opacity: 0.6, marginTop: 2 }}>
                        {p.networkingRole}
                      </div>
                    )}
                  </div>

                  {/* Checkbox */}
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      border: "2px solid",
                      borderColor: selected ? "#4ADE80" : "rgba(255,255,255,0.3)",
                      background: selected ? "#4ADE80" : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      transition: "all 0.18s",
                    }}
                  >
                    {selected && (
                      <span style={{ color: "#062317", fontSize: 13, fontWeight: 700 }}>
                        ✓
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div style={{ fontSize: 13, opacity: 0.7 }}>
              {selectedIds.length > 0
                ? `${selectedIds.length} selected`
                : "Select participants to invite"}
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: "10px 18px",
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,0.15)",
                  background: "transparent",
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleInvite}
                disabled={selectedIds.length === 0 || loading}
                style={{
                  padding: "10px 20px",
                  borderRadius: 10,
                  border: "none",
                  background:
                    selectedIds.length === 0 || loading
                      ? "rgba(74,222,128,0.3)"
                      : "linear-gradient(135deg, #4ADE80, #16A34A)",
                  color: "#062317",
                  cursor:
                    selectedIds.length === 0 || loading
                      ? "not-allowed"
                      : "pointer",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {loading ? "Sending..." : `Send Invite${selectedIds.length > 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}